import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { RUN_TOOL_CARDS } from '../../components/screens/RunToolsScreen';
import type { RunToolCard } from '../../components/screens/helpers/runScreenTypes';
import type { AppPlan } from './appPlans';
import { IconArrowLeft, IconChevronDown, IconClose, IconMinus, IconPlus, IconRepeat } from './appIcons';
import { AppToolMark, TOOL_GROUPS, getGroupId } from './AppToolsView';
import AppWorkflowPicker, { type AppWorkflowPickerGroup } from './AppWorkflowPicker';
import {
  MAX_PHASE_RUNS,
  MIN_PHASE_RUNS,
  WORKFLOW_PHASES,
  getInvalidPhaseIds,
  getPhaseProblem,
  getWorkflowRunCount,
  getWorkflowType,
  type AppWorkflow,
  type AppWorkflowExecution,
  type AppWorkflowPhase,
  type AppWorkflowPhaseId
} from './appWorkflowStore';
import { formatCadence, getWorkflowCadence } from './appWorkflowUsage';

type PickerKind = 'models' | 'tools';

interface OpenPicker {
  anchor: HTMLElement;
  phaseId: AppWorkflowPhaseId;
  kind: PickerKind;
}

interface AppWorkflowEditorProps {
  /** The stored workflow, or a new draft that has not been saved yet. */
  workflow: AppWorkflow;
  isNew: boolean;
  /** The subscription the cadence estimate is for. */
  plan: AppPlan;
  onSave: (workflow: AppWorkflow) => Promise<AppWorkflow>;
  onBack: () => void;
}

const TOOLS_BY_ID = new Map(RUN_TOOL_CARDS.map((tool) => [tool.id, tool]));

const MODEL_GROUPS: AppWorkflowPickerGroup[] = [{
  id: 'models',
  title: 'Models',
  options: RUN_TOOL_CARDS.filter((tool) => tool.kind === 'model')
}];

const TOOL_PICKER_GROUPS: AppWorkflowPickerGroup[] = TOOL_GROUPS
  .filter((group) => group.id !== 'models')
  .map((group) => ({
    id: group.id,
    title: group.title,
    options: RUN_TOOL_CARDS.filter((tool) => tool.kind !== 'model' && getGroupId(tool) === group.id)
  }));

const EXECUTION_OPTIONS: Array<{ id: AppWorkflowExecution; label: string; hint: string }> = [
  { id: 'parallel', label: 'Parallel', hint: 'Runs start together and their findings are merged.' },
  { id: 'sequential', label: 'Sequential', hint: 'Each run starts from the findings of the run before it.' }
];

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

const describePhase = (phase: AppWorkflowPhase): string => {
  const runs = phase.runs > 1 ? `${plural(phase.runs, 'run')}, ${phase.execution}` : '1 run';

  return [plural(phase.models.length, 'model'), plural(phase.tools.length, 'tool'), runs].join(' · ');
};

// Drafts compare by content; the timestamp changes on every save.
const toComparable = (workflow: AppWorkflow): string => JSON.stringify({ ...workflow, updatedAt: '' });

/**
 * A trigger that never moves while its picker is open, with the selection as removable chips
 * below it. Adding a chip grows the field downwards instead of pushing the trigger aside.
 */
function AppWorkflowSelectField({
  labelId,
  placeholder,
  count,
  ids,
  removeLabel,
  isOpen,
  hint,
  onOpen,
  onRemove
}: {
  labelId: string;
  placeholder: string;
  /** Selection count, with the cap when there is one, such as "2 of 3". */
  count: string;
  ids: string[];
  removeLabel: string;
  isOpen: boolean;
  hint?: ReactNode;
  onOpen: (anchor: HTMLElement) => void;
  onRemove: (id: string) => void;
}) {
  const tools = ids.map((id) => TOOLS_BY_ID.get(id)).filter((tool): tool is RunToolCard => Boolean(tool));

  return (
    <div className="app-wf-control">
      <button
        type="button"
        className="app-wf-select"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(event) => onOpen(event.currentTarget)}
      >
        <span className="app-wf-select-label">{placeholder}</span>
        {count && <span className="app-wf-select-count">{count}</span>}
        <IconChevronDown aria-hidden={true} />
      </button>
      {tools.length > 0 && (
        <div className="app-wf-chips" role="group" aria-labelledby={labelId}>
          {tools.map((tool) => (
            <span key={tool.id} className="app-wf-chip">
              <AppToolMark tool={tool} />
              <span>{tool.title}</span>
              <button
                type="button"
                className="app-wf-chip-remove"
                aria-label={`Remove ${tool.title} from ${removeLabel}`}
                onClick={() => onRemove(tool.id)}
              >
                <IconClose aria-hidden={true} />
              </button>
            </span>
          ))}
        </div>
      )}
      {hint}
    </div>
  );
}

function AppWorkflowEditor({ workflow, isNew, plan, onSave, onBack }: AppWorkflowEditorProps) {
  const [saved, setSaved] = useState<AppWorkflow | null>(isNew ? null : workflow);
  const [draft, setDraft] = useState<AppWorkflow>(workflow);
  const [collapsedPhaseIds, setCollapsedPhaseIds] = useState<Set<AppWorkflowPhaseId>>(() => new Set());
  const [openPicker, setOpenPicker] = useState<OpenPicker | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const type = getWorkflowType(draft.type);
  const invalidPhaseIds = getInvalidPhaseIds(draft);
  const isDirty = useMemo(() => !saved || toComparable(saved) !== toComparable(draft), [draft, saved]);
  const cadence = getWorkflowCadence(draft, plan.id);
  const cadenceLabel = formatCadence(cadence);
  const pickerPhase = openPicker ? draft.phases.find((phase) => phase.id === openPicker.phaseId) : undefined;

  const updatePhase = useCallback((phaseId: AppWorkflowPhaseId, change: (phase: AppWorkflowPhase) => AppWorkflowPhase) => {
    setDraft((current) => ({
      ...current,
      phases: current.phases.map((phase) => phase.id === phaseId ? change(phase) : phase)
    }));
  }, []);

  const toggleOption = useCallback((phaseId: AppWorkflowPhaseId, kind: PickerKind, id: string) => {
    updatePhase(phaseId, (phase) => {
      const current = phase[kind];

      if (current.includes(id)) {
        return { ...phase, [kind]: current.filter((entry) => entry !== id) };
      }

      // Each run uses one model. With a single run, picking another model swaps it in.
      if (kind === 'models' && phase.runs === 1) {
        return { ...phase, models: [id] };
      }

      return kind === 'models' && current.length >= phase.runs ? phase : { ...phase, [kind]: [...current, id] };
    });
  }, [updatePhase]);

  const openPickerFor = (phaseId: AppWorkflowPhaseId, kind: PickerKind, anchor: HTMLElement) => {
    setOpenPicker((current) => current?.anchor === anchor ? null : { anchor, phaseId, kind });
  };

  const togglePhase = (phaseId: AppWorkflowPhaseId) => {
    setCollapsedPhaseIds((current) => {
      const next = new Set(current);

      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
      }

      return next;
    });
  };

  const closePicker = useCallback(() => setOpenPicker(null), []);

  const save = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      const stored = await onSave({ ...draft, name: draft.name.trim() || type.title });

      setSaved(stored);
      setDraft(stored);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unable to save this workflow.');
    } finally {
      setIsSaving(false);
    }
  };

  const back = () => {
    const hasEdits = toComparable(draft) !== toComparable(saved ?? workflow);

    if (hasEdits && !window.confirm('Discard unsaved changes to this workflow?')) {
      return;
    }

    onBack();
  };

  const firstInvalidPhase = draft.phases.find((phase) => invalidPhaseIds.includes(phase.id));
  const saveStatus = firstInvalidPhase
    ? `${WORKFLOW_PHASES[firstInvalidPhase.id].title} ${getPhaseProblem(firstInvalidPhase) ?? ''}`
    : cadence.count === 0
      ? `This workflow is too large for ${plan.name}`
      : saveError || (saved ? 'Unsaved changes' : 'New workflow, not saved yet');

  return (
    <div className="app-wf-page">
      <div className="app-scroll">
        <div className="app-wf-editor">
          <button type="button" className="app-wf-back" onClick={back}>
            <IconArrowLeft aria-hidden={true} />
            All workflows
          </button>

          <div className="app-wf-intro">
            <input
              className="app-wf-name"
              value={draft.name}
              placeholder={type.title}
              aria-label="Workflow name"
              maxLength={80}
              onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
            />
            <input
              className="app-wf-description"
              value={draft.description}
              placeholder="Add a description"
              aria-label="Workflow description"
              maxLength={200}
              onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
            />
            <div className="app-wf-meta">
              <span className="app-status-pill">{type.title}</span>
              <span>{plural(draft.phases.length, 'phase')}</span>
              <span aria-hidden={true}>·</span>
              <span>{plural(getWorkflowRunCount(draft), 'run')}</span>
              <span aria-hidden={true}>·</span>
              <span
                className={`app-wf-cadence${cadence.count === 0 ? ' over' : ''}`}
                title={cadence.count === 0
                  ? `Too heavy for the ${plan.name} plan. Use lighter models, fewer tools or fewer runs.`
                  : `Estimate for the ${plan.name} plan. Heavier models, more tools and more runs lower it.`}
              >
                <IconRepeat aria-hidden={true} />
                {/* Keyed so a new estimate fades in, showing which edit changed it. */}
                <strong key={cadenceLabel}>{cadenceLabel}</strong>
                {cadence.count > 0 && <span>on {plan.name}</span>}
              </span>
            </div>
          </div>

          <ol className="app-wf-phases">
            {draft.phases.map((phase, index) => {
              const info = WORKFLOW_PHASES[phase.id];
              const isExpanded = !collapsedPhaseIds.has(phase.id);
              const isInvalid = invalidPhaseIds.includes(phase.id);
              const problem = getPhaseProblem(phase);
              const isRunsHeldByModels = phase.runs > MIN_PHASE_RUNS && phase.runs <= phase.models.length;
              const bodyId = `app-wf-phase-${phase.id}`;
              const execution = EXECUTION_OPTIONS.find((option) => option.id === phase.execution) ?? EXECUTION_OPTIONS[0];

              return (
                <li key={phase.id} className={`app-wf-phase${isExpanded ? ' expanded' : ''}${isInvalid ? ' invalid' : ''}`}>
                  <span className="app-wf-step" aria-hidden={true}>{index + 1}</span>
                  <div className="app-wf-card">
                    <button
                      type="button"
                      className="app-wf-card-head"
                      aria-expanded={isExpanded}
                      aria-controls={bodyId}
                      onClick={() => togglePhase(phase.id)}
                    >
                      <span className="app-wf-card-copy">
                        <strong>{info.title}</strong>
                        <span>{isExpanded ? info.description : describePhase(phase)}</span>
                      </span>
                      <IconChevronDown aria-hidden={true} className="app-wf-card-chevron" />
                    </button>

                    {isExpanded && (
                      <div id={bodyId} className="app-wf-card-body">
                        <div className="app-wf-field">
                          <span className="app-wf-label" id={`${bodyId}-models`}>Models</span>
                          <AppWorkflowSelectField
                            labelId={`${bodyId}-models`}
                            placeholder="Select models"
                            count={`${phase.models.length} of ${phase.runs}`}
                            ids={phase.models}
                            removeLabel={info.title}
                            isOpen={openPicker?.phaseId === phase.id && openPicker.kind === 'models'}
                            hint={problem && (
                              <span className="app-wf-hint error">
                                {phase.models.length === 0
                                  ? 'Select at least one model.'
                                  : 'Each run uses one model. Add runs or remove a model.'}
                              </span>
                            )}
                            onOpen={(anchor) => openPickerFor(phase.id, 'models', anchor)}
                            onRemove={(id) => toggleOption(phase.id, 'models', id)}
                          />
                        </div>

                        <div className="app-wf-field">
                          <span className="app-wf-label" id={`${bodyId}-tools`}>Skills and tools</span>
                          <AppWorkflowSelectField
                            labelId={`${bodyId}-tools`}
                            placeholder="Select skills and tools"
                            count={phase.tools.length > 0 ? String(phase.tools.length) : ''}
                            ids={phase.tools}
                            removeLabel={info.title}
                            isOpen={openPicker?.phaseId === phase.id && openPicker.kind === 'tools'}
                            onOpen={(anchor) => openPickerFor(phase.id, 'tools', anchor)}
                            onRemove={(id) => toggleOption(phase.id, 'tools', id)}
                          />
                        </div>

                        <div className="app-wf-field">
                          <span className="app-wf-label" id={`${bodyId}-runs`}>Runs</span>
                          <div className="app-wf-control">
                            <div className="app-wf-stepper" role="group" aria-labelledby={`${bodyId}-runs`}>
                              <button
                                type="button"
                                aria-label="Fewer runs"
                                title={isRunsHeldByModels ? 'Remove a model to use fewer runs' : undefined}
                                disabled={phase.runs <= Math.max(MIN_PHASE_RUNS, phase.models.length)}
                                onClick={() => updatePhase(phase.id, (current) => ({ ...current, runs: current.runs - 1 }))}
                              >
                                <IconMinus aria-hidden={true} />
                              </button>
                              <output aria-live="polite">{phase.runs}</output>
                              <button
                                type="button"
                                aria-label="More runs"
                                disabled={phase.runs >= MAX_PHASE_RUNS}
                                onClick={() => updatePhase(phase.id, (current) => ({ ...current, runs: current.runs + 1 }))}
                              >
                                <IconPlus aria-hidden={true} />
                              </button>
                            </div>
                            {isRunsHeldByModels && <span className="app-wf-hint">Each model needs its own run.</span>}
                          </div>
                        </div>

                        <div className="app-wf-field">
                          <span className="app-wf-label" id={`${bodyId}-execution`}>Execution</span>
                          <div className="app-wf-control">
                            <div className="app-wf-segmented" role="radiogroup" aria-labelledby={`${bodyId}-execution`}>
                              {EXECUTION_OPTIONS.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  role="radio"
                                  aria-checked={phase.execution === option.id}
                                  className={phase.execution === option.id ? 'active' : undefined}
                                  disabled={phase.runs === 1}
                                  onClick={() => updatePhase(phase.id, (current) => ({ ...current, execution: option.id }))}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                            <span className="app-wf-hint">
                              {phase.runs === 1 ? 'Applies when the phase has more than one run.' : execution.hint}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {isDirty && (
        <div className="app-scope-bar">
          <span className={firstInvalidPhase || cadence.count === 0 || saveError ? 'app-wf-bar-error' : undefined}>
            {saveStatus}
          </span>
          {saved && (
            <button type="button" className="app-plan-run" onClick={() => setDraft(saved)}>
              Discard
            </button>
          )}
          <button
            type="button"
            className="app-plan-run primary"
            disabled={Boolean(firstInvalidPhase) || cadence.count === 0 || isSaving}
            onClick={() => void save()}
          >
            {isSaving ? 'Saving...' : saved ? 'Save changes' : 'Save workflow'}
          </button>
        </div>
      )}

      {openPicker && pickerPhase && (
        <AppWorkflowPicker
          anchor={openPicker.anchor}
          label={`${WORKFLOW_PHASES[openPicker.phaseId].title} ${openPicker.kind === 'models' ? 'models' : 'skills and tools'}`}
          groups={openPicker.kind === 'models' ? MODEL_GROUPS : TOOL_PICKER_GROUPS}
          selected={pickerPhase[openPicker.kind]}
          {...(openPicker.kind === 'models' ? {
            limit: pickerPhase.runs,
            limitNote: pickerPhase.runs === 1 ? 'One model for one run' : `Up to ${pickerPhase.runs} models, one per run`,
            limitReason: 'Add a run to select another model'
          } : {})}
          onToggle={(id) => toggleOption(openPicker.phaseId, openPicker.kind, id)}
          onClose={closePicker}
        />
      )}
    </div>
  );
}

export default AppWorkflowEditor;
